/**
 * Lazy Controller Proxy
 *
 * Tạo một "controller giả" đứng thay cho controller thật của từng app (CapCut, Canva, Figma...).
 * Mã JS + CSS + HTML của app chỉ được tải khi người dùng thực sự mở app đó (dynamic import),
 * giúp lần tải trang đầu tiên nhẹ hơn nhiều.
 *
 * - `ensure()`            : tải module app (nếu chưa tải) và trả về instance thật.
 * - `asyncMethods`        : gọi trước khi tải -> tự tải rồi mới gọi (vd: activate, showRoute).
 * - `noopMethods`         : gọi trước khi tải -> bỏ qua an toàn (vd: deactivate khi app chưa từng mở).
 * - Gán thuộc tính trước khi tải (vd: onPanicExit) -> được lưu lại và gắn vào instance sau khi tạo.
 */
const RELOAD_FLAG = 'lazy_chunk_reload_once';

function isChunkLoadError(err) {
  const msg = String(err?.message || err || '');
  return /dynamically imported module|Importing a module script failed|error loading dynamically|Failed to fetch/i.test(msg);
}

export function createLazyController(name, loader, { asyncMethods = [], noopMethods = [] } = {}) {
  let instance = null;
  let loading = null;
  const pending = {};

  const ensure = () => {
    if (instance) return Promise.resolve(instance);
    if (!loading) {
      loading = loader()
        .then((created) => {
          Object.assign(created, pending);
          instance = created;
          window[`${name}`] = instance;
          try { sessionStorage.removeItem(RELOAD_FLAG); } catch (e) {}
          // Cho các module khác (SecurityGuard...) biết DOM mới vừa được thêm
          window.dispatchEvent(new Event('theme-changed'));
          return instance;
        })
        .catch((err) => {
          loading = null;
          console.error(`[LazyApp] Không tải được ${name}:`, err);
          // Sau khi deploy bản mới, file chunk cũ có thể không còn -> tải lại trang 1 lần
          if (isChunkLoadError(err)) {
            try {
              if (!sessionStorage.getItem(RELOAD_FLAG)) {
                sessionStorage.setItem(RELOAD_FLAG, '1');
                window.location.reload();
              }
            } catch (e) {}
          }
          throw err;
        });
    }
    return loading;
  };

  const noop = () => undefined;

  return new Proxy({}, {
    get(_, prop) {
      if (prop === 'ensure') return ensure;
      if (prop === 'isLoaded') return Boolean(instance);
      if (instance) {
        const value = instance[prop];
        return typeof value === 'function' ? value.bind(instance) : value;
      }
      if (prop === 'then') return undefined; // không phải Promise
      if (asyncMethods.includes(prop)) {
        return (...args) => ensure().then((inst) => inst[prop](...args));
      }
      if (noopMethods.includes(prop)) return noop;
      return pending[prop];
    },
    set(_, prop, value) {
      if (instance) instance[prop] = value;
      else pending[prop] = value;
      return true;
    },
    has(_, prop) {
      return instance ? prop in instance : prop in pending;
    }
  });
}

/**
 * Chèn HTML của app vào khung view rỗng (chỉ chèn 1 lần).
 */
export function injectViewHtml(viewId, html) {
  const view = document.getElementById(viewId);
  if (view && !view.dataset.lazyMounted) {
    view.innerHTML = html;
    view.dataset.lazyMounted = 'true';
  }
  return view;
}
